import { useState, useEffect, useRef, useCallback } from '@wordpress/element';
import { useSelect, useDispatch }                   from '@wordpress/data';
import { __, sprintf }                             from '@wordpress/i18n';
import { STORE_NAME }                               from '../../../store';
import { fetchProducts }                            from '../../../utils/api';
import { syncFiltersToURL, classNames } from '../../../utils/helpers';
import ProductCard    from './ProductCard';
import Pagination     from './Pagination';
import SkeletonLoader from './SkeletonLoader';

export default function ProductGrid( { attributes, instanceId } ) {
	const {
		columnsDesktop, columnsTablet, columnsMobile, gap = 24, gapTablet = 16, gapMobile = 12,
		perPage, paginationType, infiniteLoaderGif = '', infiniteLoaderDelay = 0,
		paginationBgColor, paginationTextColor, paginationBorderWidth, paginationBorderColor,
		paginationBorderRadius, paginationActiveBgColor, paginationActiveTextColor,
		paginationGap, paginationAlign, paginationBtnPadding,
		arrowStyle, arrowCustomStyle, arrowBgColor, arrowTextColor,
		arrowBorderWidth, arrowBorderColor, arrowBorderRadius,
		loadMoreText, loadMoreFullWidth, loadMoreMinWidth, loadMoreFontSize, loadMorePadding,
		loadMoreBgColor, loadMoreTextColor, loadMoreBorderWidth, loadMoreBorderColor,
		loadMoreBorderRadius, loadMoreHoverBgColor, loadMoreHoverTextColor, loadMoreHoverBorderColor,
	} = attributes;

	const filters    = useSelect( ( select ) => select( STORE_NAME ).getFilters(), [] );
	const { setPage } = useDispatch( STORE_NAME );

	const [ initialData ] = useState( () => {
		const el = document.querySelector( '[data-block="mwsb/product-grid"][data-initial-products]' );
		if ( ! el ) return null;
		const data = JSON.parse( el.getAttribute( 'data-initial-products' ) || 'null' );
		el.removeAttribute( 'data-initial-products' );
		return data;
	} );

	const [ products,    setProducts ]    = useState( initialData?.products    ?? [] );
	const [ total,       setTotal ]       = useState( initialData?.total        ?? 0 );
	const [ totalPages,  setTotalPages ]  = useState( initialData?.total_pages  ?? 0 );
	const [ loading,     setLoading ]     = useState( ! initialData );
	const [ loadingMore, setLoadingMore ] = useState( false );
	const observerRef    = useRef( null );
	const sentinelRef    = useRef( null );
	const abortRef       = useRef( null );
	const loadingMoreRef = useRef( false );
	const pageRef        = useRef( 1 );
	const totalPagesRef  = useRef( 0 );
	const delayRef       = useRef( infiniteLoaderDelay );
	const delayTimerRef  = useRef( null );
	const resultCacheRef = useRef( new Map() );

	// Keep refs in sync with state so the IntersectionObserver callback
	// always reads the latest values without stale closures.
	useEffect( () => { loadingMoreRef.current = loadingMore; },        [ loadingMore ] );
	useEffect( () => { pageRef.current        = filters.page; },       [ filters.page ] );
	useEffect( () => { totalPagesRef.current  = totalPages; },         [ totalPages ] );
	useEffect( () => { delayRef.current       = infiniteLoaderDelay; }, [ infiniteLoaderDelay ] );

	const load = useCallback( async ( opts = {} ) => {
		const { append = false, page = filters.page } = opts;
		const cacheKey = filterParamStr + ':' + page;
		const hit = resultCacheRef.current.get( cacheKey );
		if ( hit ) {
			setTotal( hit.total );
			setTotalPages( hit.total_pages );
			setProducts( ( prev ) => append ? [ ...prev, ...hit.products ] : hit.products );
			syncFiltersToURL( { ...filters, page } );
			setLoading( false );
			setLoadingMore( false );
			return;
		}

		if ( abortRef.current ) abortRef.current.abort();
		abortRef.current = new AbortController();

		if ( ! append ) setLoading( true );
		else setLoadingMore( true );

		try {
			const data = await fetchProducts( {
				...filters,
				page,
				perPage,
				orderby: filters.orderby !== 'menu_order' ? filters.orderby : attributes.defaultOrderby,
				categories: filters.categories.length ? filters.categories : attributes.defaultCategory,
			}, abortRef.current.signal );

			if ( ! data ) return;

			resultCacheRef.current.set( cacheKey, data );
			setTotal( data.total );
			setTotalPages( data.total_pages );
			setProducts( ( prev ) => append ? [ ...prev, ...data.products ] : data.products );
			syncFiltersToURL( { ...filters, page } );
		} finally {
			setLoading( false );
			setLoadingMore( false );
		}
	}, [ filters, perPage, attributes.defaultOrderby, attributes.defaultCategory ] );

	// Always-current ref so the observer never needs `load` as a dependency.
	const loadRef = useRef( load );
	useEffect( () => { loadRef.current = load; } );

	// Build a stable key from non-page filters only.
	// This effect should reset products when real filters change, but NOT when
	// the page advances during load-more / infinite scroll.
	const filterParamStr = JSON.stringify( {
		search:     filters.search,
		categories: filters.categories,
		tags:       filters.tags,
		minPrice:   filters.minPrice,
		maxPrice:   filters.maxPrice,
		orderby:    filters.orderby,
	} );

	useEffect( () => {
		if ( initialData ) {
			resultCacheRef.current.set( filterParamStr + ':1', initialData );
		}
	}, [] ); // eslint-disable-line react-hooks/exhaustive-deps

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect( () => {
		load();
	}, [ filterParamStr ] ); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect( () => {
		if ( paginationType !== 'infinite' || ! sentinelRef.current ) return;

		observerRef.current = new IntersectionObserver(
			( entries ) => {
				if (
					entries[ 0 ].isIntersecting &&
					! loadingMoreRef.current &&
					pageRef.current < totalPagesRef.current
				) {
					// Optimistically advance the ref before the async setPage completes
					// so a second intersection within the same tick cannot double-fire.
					const next = pageRef.current + 1;
					pageRef.current = next;
					loadingMoreRef.current = true;
					setLoadingMore( true );

					delayTimerRef.current = setTimeout( () => {
						setPage( next );
						loadRef.current( { append: true, page: next } );
					}, delayRef.current );
				}
			},
			{ rootMargin: '0px 0px 300px 0px', threshold: 0 }
		);

		observerRef.current.observe( sentinelRef.current );
		return () => {
			observerRef.current?.disconnect();
			clearTimeout( delayTimerRef.current );
		};
	}, [ paginationType, setPage, loading ] ); // `loading` ensures observer is created after the sentinel div mounts

	const gridStyle = {
		'--mwsb-gap':             `${ gap }px`,
		'--mwsb-gap-tablet':      `${ gapTablet }px`,
		'--mwsb-gap-mobile':      `${ gapMobile }px`,
		'--mwsb-cols-desktop':    columnsDesktop,
		'--mwsb-cols-tablet':     columnsTablet,
		'--mwsb-cols-mobile':     columnsMobile,
	};

	const ALIGN_MAP = { left: 'flex-start', center: 'center', right: 'flex-end' };
	const paginationStyle = {
		...( paginationAlign     && { '--mwsb-pag-align':        ALIGN_MAP[ paginationAlign ] || 'center' } ),
		...( paginationGap != null && { '--mwsb-pag-gap':        `${ paginationGap }px` } ),
		...( paginationBgColor   && { '--mwsb-pag-bg':           paginationBgColor } ),
		...( paginationTextColor && { '--mwsb-pag-color':        paginationTextColor } ),
		...( paginationBorderWidth != null && { '--mwsb-pag-border-width': `${ paginationBorderWidth }px` } ),
		...( paginationBorderColor && { '--mwsb-pag-border-color': paginationBorderColor } ),
		...( paginationBorderRadius != null && { '--mwsb-pag-radius': `${ paginationBorderRadius }px` } ),
		...( paginationActiveBgColor   && { '--mwsb-pag-active-bg':    paginationActiveBgColor } ),
		...( paginationActiveTextColor && { '--mwsb-pag-active-color': paginationActiveTextColor } ),
		...( paginationBtnPadding?.top    && { '--mwsb-pag-pad-top':    paginationBtnPadding.top } ),
		...( paginationBtnPadding?.right  && { '--mwsb-pag-pad-right':  paginationBtnPadding.right } ),
		...( paginationBtnPadding?.bottom && { '--mwsb-pag-pad-bottom': paginationBtnPadding.bottom } ),
		...( paginationBtnPadding?.left   && { '--mwsb-pag-pad-left':   paginationBtnPadding.left } ),
		...( arrowCustomStyle && arrowBgColor     && { '--mwsb-arrow-bg':           arrowBgColor } ),
		...( arrowCustomStyle && arrowTextColor   && { '--mwsb-arrow-color':        arrowTextColor } ),
		...( arrowCustomStyle                     && { '--mwsb-arrow-border-width': `${ arrowBorderWidth ?? 1 }px` } ),
		...( arrowCustomStyle && arrowBorderColor && { '--mwsb-arrow-border-color': arrowBorderColor } ),
		...( arrowCustomStyle                     && { '--mwsb-arrow-radius':       `${ arrowBorderRadius ?? 4 }px` } ),
	};

	const loadMoreStyle = {
		...( loadMoreFullWidth
			? { '--mwsb-lm-width': '100%', '--mwsb-lm-min-width': 'unset' }
			: { '--mwsb-lm-min-width': `${ loadMoreMinWidth ?? 160 }px` }
		),
		'--mwsb-lm-font-size':    `${ loadMoreFontSize ?? 14 }px`,
		'--mwsb-lm-border-width': `${ loadMoreBorderWidth ?? 2 }px`,
		'--mwsb-lm-radius':       `${ loadMoreBorderRadius ?? 4 }px`,
		...( loadMorePadding?.top    && { '--mwsb-lm-pad-top':    loadMorePadding.top } ),
		...( loadMorePadding?.right  && { '--mwsb-lm-pad-right':  loadMorePadding.right } ),
		...( loadMorePadding?.bottom && { '--mwsb-lm-pad-bottom': loadMorePadding.bottom } ),
		...( loadMorePadding?.left   && { '--mwsb-lm-pad-left':   loadMorePadding.left } ),
		...( loadMoreBgColor             && { '--mwsb-lm-bg':                loadMoreBgColor } ),
		...( loadMoreTextColor           && { '--mwsb-lm-color':             loadMoreTextColor } ),
		...( loadMoreBorderColor         && { '--mwsb-lm-border-color':      loadMoreBorderColor } ),
		...( loadMoreHoverBgColor        && { '--mwsb-lm-hover-bg':          loadMoreHoverBgColor } ),
		...( loadMoreHoverTextColor      && { '--mwsb-lm-hover-color':       loadMoreHoverTextColor } ),
		...( loadMoreHoverBorderColor    && { '--mwsb-lm-hover-border-color':loadMoreHoverBorderColor } ),
	};

	if ( loading ) {
		return (
			<SkeletonLoader
				count={ perPage }
				columnsDesktop={ columnsDesktop }
				columnsTablet={ columnsTablet }
				columnsMobile={ columnsMobile }
				gap={ gap }
				gapTablet={ gapTablet }
				gapMobile={ gapMobile }
			/>
		);
	}

	if ( ! products.length ) {
		return (
			<div className="mwsb-no-products">
				<p>{ __( 'No products found.', 'mwsb' ) }</p>
			</div>
		);
	}

	return (
		<div className="mwsb-product-grid-wrap" data-instance={ instanceId }>
			<div
				className={ classNames( 'mwsb-grid', `mwsb-cols-${ columnsDesktop }`, `mwsb-cols-tablet-${ columnsTablet }`, `mwsb-cols-mobile-${ columnsMobile }` ) }
				style={ gridStyle }
			>
				{ products.map( ( product, index ) => (
					<div
						key={ product.id }
						className="mwsb-card-enter"
						style={ { '--col': index % columnsDesktop } }
					>
						<ProductCard
							product={ product }
							settings={ attributes }
						/>
					</div>
				) ) }
			</div>

			{ paginationType === 'pagination' && totalPages > 1 && (
				<Pagination
					currentPage={ filters.page }
					totalPages={ totalPages }
					onPageChange={ ( page ) => { setPage( page ); load( { page } ); } }
					style={ paginationStyle }
					arrowStyle={ arrowStyle }
				/>
			) }

			{ paginationType === 'loadmore' && filters.page < totalPages && (
				<div className="mwsb-load-more-wrap" style={ loadMoreStyle }>
					<button
						className={ classNames( 'mwsb-btn mwsb-btn--load-more', loadingMore && 'mwsb-btn--loading' ) }
						onClick={ () => {
							const next = filters.page + 1;
							setPage( next );
							load( { append: true, page: next } );
						} }
						disabled={ loadingMore }
					>
						{ loadingMore ? <><span className="mwsb-spinner" /> { __( 'Loading…', 'mwsb' ) }</> : ( loadMoreText || __( 'Load More', 'mwsb' ) ) }
					</button>
					<p className="mwsb-load-more-count">
						{ sprintf( __( 'Showing %1$d of %2$d products', 'mwsb' ), products.length, total ) }
					</p>
				</div>
			) }

			{ paginationType === 'infinite' && (
				<div ref={ sentinelRef } className="mwsb-infinite-sentinel">
					{ loadingMore && (
						infiniteLoaderGif
							? <img src={ infiniteLoaderGif } alt="" className="mwsb-gif-loader" />
							: <div className="mwsb-dots-loader"><span /><span /><span /></div>
					) }
				</div>
			) }
		</div>
	);
}
